// @core/stores/subscription/SubscriptionAddonStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  AddOn,
  CreateAddOnRequest,
  UpdateAddOnRequest,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionAddonStore {
  // List state
  items: AddOn[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: AddOn | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, AddOn>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): AddOn | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: AddOn | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create addon
   */
  async CreateAddOn(data: CreateAddOnRequest): Promise<AddOn> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.CreateAddOn(data);
      const item = response.addon;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete addon
   */
  async DeleteAddOn(addon_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeleteAddOn(addon_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== addon_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === addon_id) {
          this.currentItem = null;
        }

        this.removeFromCache(addon_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get addon details
   */
  async GetAddOn(addon_id: string): Promise<AddOn> {
    // Check cache first
    const cached = this.cache.get(addon_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetAddOn(addon_id);
      const item = response.addon;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List addons
   */
  async ListAddOns(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListAddOns(params);

      runInAction(() => {
        this.items = response.addons?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update addon
   */
  async UpdateAddOn(addon_id: string, data: UpdateAddOnRequest): Promise<AddOn> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.UpdateAddOn(addon_id, data);
      const item = response.addon;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const subscriptionAddonStore = new SubscriptionAddonStore();
